import { diff } from 'json-diff-ts';
import { capitalize } from 'vue';

import TableColumn from '@/common/components/controls/table/TableColumn';
import TableColumnTypes from '@/common/components/controls/table/TableColumnTypes';
import Exception from '@/common/models/Exception';
import { IOption } from '@/common/models/Option';
import { FilterCollection, FilterGroup, QueryParams } from '@/common/models/query-params';
import { areArraysEqual, areObjectsEqual, DASH, formatNumber } from '@/globals';
import { IExtraValidation } from '@/validator/Validatable';
import i18n from '@/vueI18n';

interface IEntityProperty<T = unknown> {
  action?: (entity: T) => any;
  editablePropertyAcceptsMultipleValues?: boolean;
  editableClassName?: string;
  editablePropertyName?: string;
  getExtraValidation?: (entity: T) => Record<string, IExtraValidation>;
  isEditableHandler?: (entity?: T) => boolean;
  label?: string | ((entity?: T) => string);
  mutation?: string | ((payload: any) => void);
  postMutationCallback?: (value?: any, entity?: T) => void;
  preMutationTransform?: (...args: any[]) => any;
  preActionTransform?: (...args: any[]) => any;
  property?: string;
  source?: Array<any> | (({ queryParams }) => Promise<any>) | Promise<any> | (() => Array<IOption>);
  sourceFilterProperty?: string;
  sourceGroupValues?: string;
  sourceGroupLabel?: string;
  sourceID?: string;
  sourceLabel?: string;
  sourceSearchTermToFiltersTransform?: (search?: string) => FilterGroup[];
  sourceSupportsAutoComplete?: boolean;
  type: string | ((entity?: T) => string);
  inputElementWidth?: string;
  validationPropertyName?: string;
  canAffectAdDeliveryWhenModified?: boolean;
  entityValueGetterTransform?: (value?: any) => any;
  preventEditingEntityProperties?: string[];
  areSourceGroupParentsSelectable?: boolean;
  checkForEmptyness?: boolean;
  isMoney?: boolean;
  bulkCreateOrderIndex?: number;
  isProxyForProperty?: EntityProperty<T>;
  proxyPropertyMapper?: (ids?: string[]) => Record<string, string>;
  areValuesEqual?: (from?: any, to?: any) => boolean;
  bulkInputNote?: string;
  bulkInputExamples?: string[];
  defaultValue?: any;
  mutatesProperties?: EntityProperty<any>[];
  isInlineEditingAvailable?: boolean;
  inlineEditingOptionsSource?: () => Promise<any[]>; // Source to be used by TableCellEditor dropdown if the source for the filter and inline edit are differents (Venue types filter source and inline edit source have different structures and need different sources)
  sourceQueryParamsForReadOnly?: (entity) => QueryParams;
  allowExclusions?: boolean;
  fallbackToString?: (value) => string;
  toStringOverride?: (entity: T) => string;
  ignoreUnmatchedValuesInSource?: boolean;
  handleInlineEditUpdateOutsideOfTable?: (entity: T, value: string) => void;
}

export default class EntityProperty<T> implements IEntityProperty<T> {
  action: (entity: T) => any; // A Vuex action to save the entity.
  editablePropertyAcceptsMultipleValues: boolean; // Whether this property can accept multiple values.
  editableClassName: string; // The class name to use for the editable element.
  editablePropertyName: string; // The name of the property to use when editing.
  getExtraValidation: (entity: T) => Record<string, IExtraValidation>; // Extra validation rules.
  isEditableHandler: (entity?: T) => boolean; // A function to determine if this property is editable.
  label: string | ((entity?: T) => string); // The label to display for this property.
  mutation: string | ((payload: any) => void); // The mutation to use when updating this property.
  postMutationCallback: <V>(value?: V, entity?: T) => void; // A callback to run after the mutation.
  preMutationTransform: (...args: any[]) => any; // A function to transform the value before mutation.
  preActionTransform: (...args: any[]) => any; // A function to transform the value before action.
  property?: string; // The property name.
  source: Array<any> | (({ queryParams }) => Promise<any>) | Promise<any> | (() => Array<IOption>); // The source of the data.
  sourceFilterProperty: string; // The property to use when filtering the source.
  sourceGroupValues: string; // When the source has a tree hierarchy, this is the property to use for the children.
  sourceGroupLabel: string; // When the source has a tree hierarchy, this is the property to use for the name of the group.
  sourceID: string; // The property to use for the ID of the source.
  sourceLabel: string; // The property to use for the label of the source.
  sourceSearchTermToFiltersTransform: (search: string) => FilterGroup[]; // A function to transform the search term to filters.
  sourceSupportsAutoComplete: boolean; // Whether the source supports auto-complete.
  type: string | ((entity: T) => string); // The type of the property.
  inputElementWidth: string; // The width of the input element.
  validationPropertyName: string; // The property name to use for validation.
  canAffectAdDeliveryWhenModified: boolean; // Whether this property can affect ad delivery.
  entityValueGetterTransform?: (value?: any) => any; // A function to transform the value when getting it from the entity.
  preventEditingEntityProperties: Array<string>; // Properties that should not be editable whenever this property is being edited (Bulk).
  areSourceGroupParentsSelectable: boolean; // Whether the parent groups of the source are selectable.
  checkForEmptyness: boolean; // Whether to check for emptyness.
  isMoney: boolean; // Whether this property is money.
  bulkCreateOrderIndex: number; // The order index for fields in bulk create.
  isProxyForProperty: EntityProperty<T>; // See Unit siteExternalID
  proxyPropertyMapper: (ids: string[]) => Record<string, string>; // A function to map the proxy property.
  areValuesEqual: (from, to) => boolean; // A function to determine if the values are equal.
  bulkInputNote: string; // Text displayed in the header of worksheet and in the reference CSV for Bulk V4.
  bulkInputExamples; // Examples displayed in the header of XLS worksheet and in the reference CSV for Bulk V4.
  defaultValue: any; // Default value for the property.
  mutatesProperties: EntityProperty<any>[]; // Whenever this property is updated it will mutate the properties in this collection. (e.g.: budget & target impressions mutate budgetMode)
  isInlineEditingAvailable: boolean;
  inlineEditingOptionsSource?: () => Promise<any[]>;
  sourceQueryParamsForReadOnly?: (entity) => QueryParams;
  allowExclusions?: boolean;
  fallbackToString?: (value) => string; // A function to use when a value cannot be mapped to the source, an example of this would be `-1` for the accountID of a campaign, you'd want to return the new account name..
  toStringOverride?: (entity: T) => string; // A function to transform the value to a string. i.e.: minSecondsBetweenPlays can be a number of seconds of a string like '1 exposure every 2 hour'.
  ignoreUnmatchedValuesInSource?: boolean; // Whether to ignore unmatched values in the source, an example is location_ids (level).
  handleInlineEditUpdateOutsideOfTable?: (entity: T, value: string) => void; // Whether to handle the update outside of the table.

  constructor(options: IEntityProperty<T>) {
    const validOptions = [
      'action',
      'editablePropertyAcceptsMultipleValues',
      'editableClassName',
      'editablePropertyName',
      'getExtraValidation',
      'isEditableHandler',
      'label',
      'mutation',
      'postMutationCallback',
      'preMutationTransform',
      'preActionTransform',
      'property',
      'source',
      'sourceFilterProperty',
      'sourceGroupValues',
      'sourceGroupLabel',
      'sourceID',
      'sourceLabel',
      'sourceSupportsAutoComplete',
      'sourceSearchTermToFiltersTransform',
      'type',
      'inputElementWidth',
      'validationPropertyName',
      'canAffectAdDeliveryWhenModified',
      'entityValueGetterTransform',
      'preventEditingEntityProperties',
      'areSourceGroupParentsSelectable',
      'checkForEmptyness',
      'isMoney',
      'bulkCreateOrderIndex',
      'isProxyForProperty',
      'proxyPropertyMapper',
      'areValuesEqual',
      'bulkInputNote',
      'bulkInputExamples',
      'defaultValue',
      'mutatesProperties',
      'isInlineEditingAvailable',
      'inlineEditingOptionsSource',
      'allowExclusions',
      'sourceQueryParamsForReadOnly',
      'fallbackToString',
      'toStringOverride',
      'ignoreUnmatchedValuesInSource',
      'handleInlineEditUpdateOutsideOfTable',
    ];
    const invalidOptions = Object.keys(options).filter((x) => !validOptions.includes(x));

    if (invalidOptions.length > 0) {
      throw new Exception(`Invalid properties: ${invalidOptions.join(', ')}`);
    }
    this.action = options.action;
    this.editablePropertyAcceptsMultipleValues = options.editablePropertyAcceptsMultipleValues;
    this.editableClassName = options.editableClassName;
    this.editablePropertyName = options.editablePropertyName;
    this.getExtraValidation = options.getExtraValidation;
    this.isEditableHandler = options.isEditableHandler;
    this.label = options.label;
    this.mutation = options.mutation;
    this.property = options.property;
    this.postMutationCallback = options.postMutationCallback;
    this.preMutationTransform = options.preMutationTransform;
    this.preActionTransform = options.preActionTransform;
    this.source = options.source;
    this.sourceFilterProperty = options.sourceFilterProperty;
    this.sourceGroupValues = options.sourceGroupValues;
    this.sourceGroupLabel = options.sourceGroupLabel;
    this.sourceID = options.sourceID || 'id';
    this.sourceLabel = options.sourceLabel || 'name';
    this.sourceSearchTermToFiltersTransform = options.sourceSearchTermToFiltersTransform;
    this.sourceSupportsAutoComplete = options.sourceSupportsAutoComplete;
    this.type = options.type;
    this.inputElementWidth = options.inputElementWidth;
    this.validationPropertyName = options.validationPropertyName;
    this.canAffectAdDeliveryWhenModified = options.canAffectAdDeliveryWhenModified;
    this.entityValueGetterTransform = options.entityValueGetterTransform;
    this.preventEditingEntityProperties = options.preventEditingEntityProperties;
    this.areSourceGroupParentsSelectable = options.areSourceGroupParentsSelectable;
    this.checkForEmptyness = options.checkForEmptyness;
    this.isMoney = options.isMoney;
    this.bulkCreateOrderIndex = options.bulkCreateOrderIndex;
    this.isProxyForProperty = options.isProxyForProperty;
    this.proxyPropertyMapper = options.proxyPropertyMapper;
    this.areValuesEqual = options.areValuesEqual || this.areValuesEqualInternal;
    this.bulkInputNote = options.bulkInputNote;
    this.bulkInputExamples = options.bulkInputExamples;
    this.defaultValue = options.defaultValue;
    this.mutatesProperties = options.mutatesProperties;
    this.isInlineEditingAvailable = options.isInlineEditingAvailable;
    this.inlineEditingOptionsSource = options.inlineEditingOptionsSource;
    this.allowExclusions = options.allowExclusions;
    this.sourceQueryParamsForReadOnly = options.sourceQueryParamsForReadOnly;
    this.fallbackToString = options.fallbackToString;
    this.toStringOverride = options.toStringOverride;
    this.ignoreUnmatchedValuesInSource = options.ignoreUnmatchedValuesInSource;
    this.handleInlineEditUpdateOutsideOfTable = options.handleInlineEditUpdateOutsideOfTable;
  }

  getTableColumn(options = {}) {
    return TableColumn.fromObject({
      ...this,
      ...options,
    });
  }

  get apiModelProperty() {
    return this.isProxyForProperty?.property || this.property || this.sourceFilterProperty;
  }

  get modelProperty() {
    if (this.isProxyForProperty != null) {
      return this.isProxyForProperty.editablePropertyName || this.isProxyForProperty.property;
    }
    return this.editablePropertyName || this.validationPropertyName || this.property;
  }

  async getSourceCache(entity: T) {
    let sourceCache;

    if (this.source instanceof Function) {
      const sourceResponse = await this.getReadOnlySource(entity);
      sourceCache = sourceResponse.items || sourceResponse;
    } else if (this.source instanceof Promise) {
      console.warn('Source is a promise');
    } else {
      sourceCache = this.source;
    }

    if (this.sourceGroupValues) {
      for (let i = 0; i < 2; i += 1) {
        // Looping twice, not expecting trees with more than 3 levels.
        sourceCache = sourceCache.reduce(
          (mem, x) => [...mem, ...(x[this.sourceGroupValues] ? [x, ...x[this.sourceGroupValues]] : [x])],
          [],
        );
      }
    }

    return sourceCache;
  }

  async toString(entity: T) {
    if (this.toStringOverride) {
      return this.toStringOverride(entity);
    }

    let value = entity[this.modelProperty];

    if (this.entityValueGetterTransform) {
      value = this.entityValueGetterTransform(value);
    }

    if (value === null || value === undefined || (value instanceof Array && value.length === 0)) return null;

    const sourceCache = await this.getSourceCache(entity);

    if (sourceCache) {
      return this.type === TableColumnTypes.list || this.editablePropertyAcceptsMultipleValues
        ? (value instanceof Array ? value : [value])
            .map(
              (v) => sourceCache.find((x) => x[this.sourceID] === v) || (this.ignoreUnmatchedValuesInSource ? null : v),
            )
            .filter((x) => x) // This is necessary because the source may not have all the values, an example is the line item countries entity property.
            .map((x) => x[this.sourceLabel] || x)
            .reduce((mem, x) => (mem ? `${mem}, ${x}` : `${x}`), null)
        : sourceCache.find((x) => x[this.sourceID] === value)?.[this.sourceLabel] ||
            (this.fallbackToString ? this.fallbackToString(entity) : DASH);
    }

    if (this.editablePropertyAcceptsMultipleValues) {
      const valueInternal = Array.isArray(value) ? value : Object.keys(value);
      return valueInternal?.map((v) => (v.toString ? v.toString() : v))?.join('|') ?? [];
    }

    const type = typeof this.type === 'function' ? this.type(entity) : this.type;
    switch (type) {
      case TableColumnTypes.date:
        return value ? new Date(value).toLocaleDateString() : DASH;
      case TableColumnTypes.datetime:
        return value ? new Date(value).toLocaleString() : DASH;
      case TableColumnTypes.time:
        return value ? new Date(value).toLocaleTimeString() : DASH;
      case TableColumnTypes.boolean:
        return capitalize(value ? i18n.global.t('actions.yes') : i18n.global.t('actions.no'));
      case TableColumnTypes.currency:
      case TableColumnTypes.money:
        return value ? formatNumber(value, '$0,0.00') : DASH;
      case TableColumnTypes.integer:
        return value ? formatNumber(value, '0,0') : DASH;
      case TableColumnTypes.percentage:
        return value ? formatNumber(value, '%0,0.00') : DASH;
      case TableColumnTypes.float:
      default:
        return value.toString ? value.toString() : value;
    }
  }

  areValuesEqualInternal(from?: any, to?: any) {
    const type = typeof this.type === 'function' ? this.type(from) : this.type;
    switch (type) {
      case TableColumnTypes.date:
      case TableColumnTypes.datetime:
        return from[this.modelProperty]?.toISO() === to[this.modelProperty]?.toISO();
      case TableColumnTypes.list:
        if (Array.isArray(from[this.modelProperty]) && Array.isArray(to[this.modelProperty])) {
          return areArraysEqual(from[this.modelProperty] || [], to[this.modelProperty] || []);
        }
        return areObjectsEqual(from[this.modelProperty] || {}, to[this.modelProperty] || {});
      case TableColumnTypes.boolean:
      case TableColumnTypes.currency:
      case TableColumnTypes.integer:
      case TableColumnTypes.float:
      case TableColumnTypes.percentage:
      default:
        return from?.[this.modelProperty] == to?.[this.modelProperty];
    }
  }

  async getDiff(from?: any, to?: any) {
    const fromSourceCache = (await this.getSourceCache(from)) || [];
    const toSourceCache = (await this.getSourceCache(to)) || [];
    const sourceCache = [...fromSourceCache, ...toSourceCache];
    let fromArray = from[this.modelProperty] instanceof Array ? from[this.modelProperty] : [from[this.modelProperty]];
    let toArray = to[this.modelProperty] instanceof Array ? to[this.modelProperty] : [to[this.modelProperty]];

    // This is a workaround for the fact that city, regions and countries are all stored in location_ids
    // If the source does not have the location_ids, we need to filter them out
    // FE-546 - https://perion.atlassian.net/browse/FE-546
    if (this.property === 'location_ids') {
      fromArray = fromArray.filter((x) => sourceCache.some((s) => s[this.sourceID] === x));
      toArray = toArray.filter((x) => sourceCache.some((s) => s[this.sourceID] === x));
    }

    const formatToMap = (list) => {
      return list.reduce(
        (mem, x) => ({
          ...mem,
          [x]: sourceCache?.find((s) => s[this.sourceID] === x)?.[this.sourceLabel] || x,
        }),
        {},
      );
    };

    // this is me cheating because using getArrayDiff will return an update if the order is different
    // converting the arrays to object works better
    const fromMap = formatToMap(fromArray);
    const toMap = formatToMap(toArray);

    return diff(fromMap, toMap);
  }

  getReadOnlySource(entity) {
    if (!(this.source instanceof Function)) {
      return this.source;
    }
    if (!this.sourceQueryParamsForReadOnly) {
      return this.source({ queryParams: QueryParams.all() });
    }
    const queryParams = this.sourceQueryParamsForReadOnly(entity);
    if (FilterCollection.areFilterValuesEmpty(queryParams.filterCollection.filters)) {
      return [];
    }
    return this.source({ queryParams });
  }

  static getIDProperty<T>(label: string, property: string) {
    return new EntityProperty<T>({
      label,
      type: TableColumnTypes.string,
      property,
    });
  }

  getLabel() {
    return this.label instanceof Function ? this.label() : this.label;
  }
}
